import { Divider, Grid } from '@mui/material';
import { Location, LocationMapper } from '@nx-smartmonkey/shared/domain';
import { RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import StopSearchIcon from '../../../../../assets/img/shared/icon-search.svg';
import BackArrowIcon from '../../../../../assets/svg/BackArrowIcon';
import { DividerWithText } from '../../../../../components/DividerWithText';
import { Modal } from '../../../../../components/Modal';
import { SearchInput } from '../../../../../components/searchInput/SearchInput';
import * as routing from '../../../../../constants/Routing';
import { Route } from '../../../../../domain/route/Route';
import { Stop } from '../../../../../domain/stops/Stop';
import { intl } from '../../../../../helpers/IntGlobalProvider';
import Button from '../../../../../react-components/Button';
import { Text } from '../../../../../react-components/Text';
import { usePermissions } from '../../../hooks/usePermissions';
import { AddStopsList } from '../AddStopsList';
import { CREATE_STOPS_INITIAL_LOCATION_KEY } from '../create-stops/CreateStopsManually';
import { StopsSearchList } from './routeStopsSearchMenuComponents/StopsSearchList';

const CreateStopButtonContainer = styled(Grid)`
  display: flex;
  background-color: ${RoutalPalette.neutral02n};
  overflow: auto;
  max-width: 100vw;
  height: auto;
  scrollbar-width: none;
  overflow-y: scroll;
  padding: 12px 20px;

  &::after {
    content: '';
    position: absolute;
    bottom: 76px;
    left: 0;
    right: 0;
    height: 30px;
    width: 100%;
    background: linear-gradient(0deg, ${RoutalPalette.neutral02n}, ${RoutalPalette.neutral02n}00);
    pointer-events: none;
  }
`;

const CreateStopButton = styled(Button)`
  background-color: ${RoutalPalette.secondary60};
  padding: 0 28px;
  height: 52px;
  border: none;
  margin: 0;
  width: 100%;
`;

interface RoutesStopsSlidingSearchProps {
  selectedRoute: Route;
  onSelectStop: (stopId: string) => void;
  isSliderVisible: boolean;
  setIsSliderVisible: (value: boolean) => void;
}

interface SortedStop {
  stop: Stop;
  occurrence: number;
}

export const RoutesStopsSearchMenu = ({
  selectedRoute,
  onSelectStop,
  isSliderVisible,
  setIsSliderVisible,
}: RoutesStopsSlidingSearchProps) => {
  const navigate = useNavigate();
  const { canManageStops } = usePermissions();

  const [filterText, setFilterText] = useState<string>(``);
  const [filtered, setFiltered] = useState<Stop[]>([]);
  const [duplicateStopsModal, setDuplicateStopsModal] = useState<Location | undefined>(undefined);

  // Lógica del input
  const handleChange = (event: string) => {
    const newText = event;
    setFilterText(newText);
    setFiltered(getFilteredStopsByOccurrence(selectedRoute.stops, filterText));
  };

  const canCreateStops = canManageStops && selectedRoute.status !== `finished`;

  // Lógica de búsqueda
  const calcOccurrenceOfWordsInSentence = (sentence: string, words: string[]): number => {
    if (!sentence) return 0;
    let s2 = sentence.replace(/[&/\\#,+()$~%.":*?<>{}]/g, ``); // Quitar caracteres especiales

    s2 = removeAccents(s2.replace(/[']/g, ` `)); //Separar palabras con apostrofes
    const sentenceWords = s2.toLowerCase().split(` `);
    let count = 0;

    words.forEach((word) => {
      sentenceWords.forEach((sentenceWord) => {
        if (sentenceWord.includes(word.toLowerCase())) {
          count++;
        }
      });
    });

    return count;
  };

  const removeAccents = (str: string) => str.normalize(`NFD`).replace(/[\u0300-\u036f]/g, ``);

  const getFilteredStopsByOccurrence = (stops: Stop[], words: string): Stop[] => {
    const words2 = words.replace(/^\s+/, ``);
    if (words2 === ``) return stops;

    let clearWords = removeAccents(words2.toLowerCase()).split(` `);
    clearWords = clearWords.filter((word) => word !== ` ` && word !== ``);

    const sortable: SortedStop[] = [];
    stops.forEach((stop) => {
      let occurrences_name;
      if (!stop.location) {
        const empty_label = intl.formatMessage({ id: `stop.label_stop.empty` });
        occurrences_name = calcOccurrenceOfWordsInSentence(empty_label as string, clearWords);
      } else occurrences_name = calcOccurrenceOfWordsInSentence(stop.location?.label as string, clearWords);
      const occurrences_label = calcOccurrenceOfWordsInSentence(stop.label as string, clearWords);
      sortable.push({ stop: stop, occurrence: occurrences_label + occurrences_name });
    });

    const compare = (a: SortedStop, b: SortedStop) => {
      return b.occurrence - a.occurrence;
    };

    const sortedStops = sortable.sort(compare);
    const firstStops = sortedStops.filter((array) => array.occurrence >= 1).map((array) => array.stop);

    return firstStops;
  };

  useEffect(() => {
    setFiltered(getFilteredStopsByOccurrence(selectedRoute.stops, filterText));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterText]); //selectedRoute.stops

  useEffect(() => {
    setFiltered(getFilteredStopsByOccurrence(selectedRoute.stops, filterText));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoute]);

  const onSelectLocation = async (stopLocation: Location) => {
    localStorage.setItem(CREATE_STOPS_INITIAL_LOCATION_KEY, JSON.stringify(LocationMapper.toDTO(stopLocation)));
    navigate(routing.PRIVATE_ROUTES.CREATE_STOPS_MANUAL.replace(`:routeId`, selectedRoute.id));
  };

  return (
    <>
      <Grid
        container
        alignItems="center"
        flexDirection="column"
        flexWrap="nowrap"
        style={{
          position: `absolute`,
          inset: `0`,
          width: `100%`,
          height: `100%`,
          zIndex: isSliderVisible ? 1006 : 1003,
          backgroundColor: isSliderVisible ? RoutalPalette.oldWhite : `transparent`,
          transition: `background-color 0.2s ease`,
          transitionDelay: `0.2s`,
          padding: `0`,
          pointerEvents: isSliderVisible ? `all` : `none`,
        }}
      >
        <div
          onClick={() => (isSliderVisible ? null : setIsSliderVisible(true))}
          style={{
            display: `flex`,
            alignContent: `right`,
            height: `48px`,
            minHeight: `48px`,
            backgroundColor: RoutalPalette.oldWhite,
            color: `black`,
            width: `calc(100% - 32px)`,
            borderRadius: `20px`,
            border: !isSliderVisible ? `1px solid rgba(0, 0, 0, 0.0)` : `1px solid rgba(0, 0, 0, 0.2)`,
            transition: `border 0.2s ease`,
            justifyContent: `center`,
            pointerEvents: `none`,
            margin: `20px`,
          }}
        >
          <Grid
            container
            alignItems="row"
            style={{
              justifyContent: `center`,
              flexWrap: `nowrap`,
              alignItems: `center`,
              flexDirection: `row`,
              paddingLeft: `10px`,
              pointerEvents: `all`,
            }}
          >
            <Grid style={{ position: `relative`, width: `25px`, height: `25px`, display: `flex` }}>
              <BackArrowIcon
                style={{
                  position: `absolute`,
                  top: 0,
                  left: 0,
                  color: RoutalPalette.secondary40,
                  fontSize: `25px`,
                  cursor: `pointer`,
                  transition: isSliderVisible ? `opacity 0.2s ease 0.2s` : `opacity 0.2s, visibility 0.2s`,
                  opacity: isSliderVisible ? `1` : `0`,
                  visibility: isSliderVisible ? `visible` : `hidden`,
                }}
                onClick={() => setIsSliderVisible(false)}
              />

              <img
                src={StopSearchIcon}
                alt="search menu icon"
                draggable="false"
                width="20"
                style={{
                  position: `absolute`,
                  top: 0,
                  left: 0,
                  transition: !isSliderVisible ? `opacity 0.2s ease 0.2s` : `opacity 0.2s, visibility 0.2s`,
                  opacity: !isSliderVisible ? `1` : `0`,
                  visibility: !isSliderVisible ? `visible` : `hidden`,
                }}
              />
            </Grid>

            <SearchInput
              onTextChange={(text: string) => handleChange(text)}
              searchLive
              voiceInput
              disableMicrophoneIfText
              // continuosVoiceInput
              inputContainerStyles={{
                margin: `0 10px`,
                backgroundColor: RoutalPalette.oldWhite,
              }}
              inputStyles={{
                backgroundColor: RoutalPalette.oldWhite,
              }}
              clearButton
              placeholder={intl.formatMessage({
                id: canCreateStops ? `driver.stop_list.input_search_ph_create` : `driver.stop_list.input_search_ph`,
              })}
            />
          </Grid>
        </div>

        <Divider
          style={{
            transition: isSliderVisible ? `opacity 0.2s ease 0.2s` : `opacity 2s visibility 0.2s`,
            opacity: isSliderVisible ? `1` : `0`,
            visibility: isSliderVisible ? `visible` : `hidden`,
            transitionDelay: isSliderVisible ? `0.2s` : `0s`,
            width: `100vw`,
            margin: `0`,
          }}
        />

        <Grid
          container
          style={{
            transition: isSliderVisible ? `opacity 0.2s ease 0.2s` : `opacity 2s visibility 0.2s`,
            opacity: isSliderVisible ? `1` : `0`,
            visibility: isSliderVisible ? `visible` : `hidden`,
            height: `calc(100% - 90px - 1px - ${canCreateStops ? 76 : 0}px)`, // 100% - header_height - divider - footer_height (if canCreateStops)
          }}
        >
          <Grid
            container
            direction="column"
            wrap="nowrap"
            style={{
              padding: `16px`,
              background: RoutalPalette.oldWhite,
              overflowY: `auto`,
              height: `100%`,
            }}
          >
            <StopsSearchList
              canCreateStops
              hasFilter={!!filterText}
              routeStops={filtered}
              onSelectStop={(stop_id) => {
                onSelectStop(stop_id);
                setIsSliderVisible(false);
              }}
            />

            {canCreateStops && filterText !== `` && (
              <>
                <DividerWithText text={intl.formatMessage({ id: `driver.add_stops` })} />

                <AddStopsList
                  icon="add"
                  searchText={filterText}
                  onSelectLocation={(location: Location) => {
                    if (
                      selectedRoute.stops.find(
                        (s) =>
                          s.location &&
                          s.location.lng &&
                          s.location.lat &&
                          location.lng === s.location.lng &&
                          location.lat === s.location.lat
                      )
                    ) {
                      setDuplicateStopsModal(location);
                    } else {
                      onSelectLocation(location);
                    }
                  }}
                  refreshSession={Boolean(canManageStops && filterText !== `` && selectedRoute.status === `in_transit`)}
                />
              </>
            )}
          </Grid>
        </Grid>

        {canCreateStops && (
          <CreateStopButtonContainer
            zIndex={isSliderVisible ? 1004 : 1003}
            style={{
              transition: isSliderVisible ? `opacity 0.2s ease 0.2s` : `opacity 2s visibility 0.2s`,
              opacity: isSliderVisible ? `1` : `0`,
              visibility: isSliderVisible ? `visible` : `hidden`,
              width: `100%`,
            }}
          >
            <Grid container direction="row" justifyContent="space-between" gap="10px" wrap="nowrap">
              <CreateStopButton
                onClick={() =>
                  navigate(routing.PRIVATE_ROUTES.CREATE_STOPS_MANUAL.replace(`:routeId`, selectedRoute.id))
                }
              >
                <Text
                  intlId="driver.stops.create_stops.add_direction"
                  defaultMessage="Añadir dirección"
                  style={{ color: RoutalPalette.oldWhite, whiteSpace: `nowrap` }}
                />
              </CreateStopButton>
              <CreateStopButton
                onClick={() => navigate(routing.PRIVATE_ROUTES.CREATE_STOPS_BULK.replace(`:routeId`, selectedRoute.id))}
              >
                <Text
                  intlId="driver.stops.create_stops.add_paste"
                  defaultMessage="Muliples paradas"
                  style={{ color: RoutalPalette.oldWhite, whiteSpace: `nowrap` }}
                />
              </CreateStopButton>
            </Grid>
          </CreateStopButtonContainer>
        )}
      </Grid>

      <Modal
        open={duplicateStopsModal !== undefined}
        onClose={() => {
          setDuplicateStopsModal(undefined);
        }}
        title={{
          intlId: `driver.stops.create_stops.add_duplicate_stop.title`,
        }}
        subtitle={{
          text: intl.formatMessage(
            {
              id: `driver.stops.create_stops.add_duplicate_stop.subtitle`,
            },
            { value: duplicateStopsModal?.label }
          ),
        }}
        buttons={[
          {
            intlId: `driver.stops.create_stops.confirm`,
            onClick: () => {
              if (duplicateStopsModal) {
                onSelectLocation(duplicateStopsModal);
              }
              setDuplicateStopsModal(undefined);
            },
          },
          {
            intlId: `cancel`,
            onClick: () => {
              setDuplicateStopsModal(undefined);
            },
            variant: `text`,
            style: {
              border: `none`,
            },
          },
        ]}
        children={<VSpacer medium />}
      />
    </>
  );
};
